import { createContext, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  DEFAULT_LANGUAGE,
  Language,
  getTranslation,
  resolveNavLanguage,
} from "@/utils/i18n";

type LanguageContextType = {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (path: string, vars?: Record<string, string | number>) => string;
};

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

/**
 * Bridges the legacy `useLanguage` API (hand-rolled key/value translations
 * for the header/nav) to the modern react-i18next picker. The picker is the
 * single source of truth for the active locale; this context just resolves
 * whatever i18next reports to the closest nav-supported language and exposes
 * the same `t()` function the rest of the legacy code already uses.
 */
export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const { i18n } = useTranslation();
  const [language, setLanguageState] = useState<Language>(() =>
    resolveNavLanguage(i18n.language || DEFAULT_LANGUAGE),
  );

  // Keep in sync when react-i18next reports a language change (picker, URL, …)
  useEffect(() => {
    const handler = (lng: string) => setLanguageState(resolveNavLanguage(lng));
    i18n.on("languageChanged", handler);
    // Initial sync in case i18next finished detecting after first paint
    setLanguageState(resolveNavLanguage(i18n.language));
    return () => {
      i18n.off("languageChanged", handler);
    };
  }, [i18n]);

  const setLanguage = (lang: Language) => {
    void i18n.changeLanguage(lang);
    setLanguageState(lang);
  };

  const t = (path: string, vars?: Record<string, string | number>): string =>
    getTranslation(language, path, vars);

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
}
